// store/cart.js
const store = {
    namespaced: true,
    state() {
        return {
            totalCount: 0,
            totalPrice: 0,
            totalDiscount: 0,
            cartProducts: null,
            cartData: {
                total_count: 0,
                total_discount: 0,
                total_price: 0,
                loyalty_give: 0,
                products: [],
            },
            isFetchingAllCartProducts: false,
            isValidCartData: false,
            cartDataErrors: null,
            browserId: null,
            cities: null,
            addresses: null,
            isShowAddedToCartNotification: false,
            deliveryAddress: null,
            canProceedWithOrder: false,
            proceedWithOrderErrors: null,
            storeId: null,
            deliveryDateAndTime: null,
            promoCode: null,
            currentDeliveryDateAndTime: null,
            isOrderCompleted: false,
            isAddressCreated: false,
            shippingAddressId: null,
            deliveryAddressErrors: null,
            isInvalidPromoCode: false,
            isSendCartProcessing: false,
            isOrderError: null,
            isFetchingProduct: false,
            isNotAuthAfterFetching: false,
            errorWithCode: null,
        };
    },
    getters: {
        getTotalCount(state) {
            return state.totalCount;
        },
        getTotalPrice(state) {
            return state.totalPrice;
        },
        getTotalDiscount(state) {
            return state.totalDiscount;
        },
        getCartProducts(state) {
            return state.cartProducts;
        },
        getCartData(state) {
            return state.cartData;
        },
        getProductsLength(state) {
            return state.cartData.products.length;
        },
        getIsFetchingCartProducts(state) {
            return state.isFetchingAllCartProducts;
        },
        getIsValidCartData(state) {
            return state.isValidCartData;
        },
        getBrowserId(state) {
            return state.browserId;
        },
        getAvailableProductForHomeDelivery(state) {
            return state.cartData.products?.filter(product => product?.home_delivery);
        },
        getProductsUnavailableToHomeDelivery(state) {
            return state.cartData?.products.filter(product => !product.home_delivery);
        },
        getProductsAvailableToPayCard(state) {
            return state.cartData.products?.filter(product => product?.card_payment_available);
        },
        getProductsUnavailableToPayCard(state) {
            return state.cartData?.products.filter(product => !product.card_payment_available);
        },
        getAllProductsWithConfirmedRecipe(state) {
            const productsWithRecipe = state.cartData?.products.filter(product => product.is_recipe);
            return productsWithRecipe?.every(product => product.prescription_checked);
        },
        getAllLoyaltyProductIds(state) {
            return state.cartData?.products.filter(product => product.is_loyalty_active).map(product => product.id);
        },
        getCities(state) {
            return state.cities;
        },
        getIsShowAddedToCartNotification(state) {
            return state.isShowAddedToCartNotification;
        },
        getAddresses(state) {
            return state.addresses;
        },
        getDeliveryAddress(state) {
            return state.deliveryAddress;
        },
        getCanProceedWithOrder(state) {
            return state.canProceedWithOrder;
        },
        getProceedWithOrderErrors(state) {
            return state.proceedWithOrderErrors;
        },
        getStoreId(state) {
            return state.storeId;
        },
        getDeliveryDateAndTime(state) {
            return state.deliveryDateAndTime;
        },
        getPromoCode(state) {
            return state.promoCode;
        },
        getCartDataErrors(state) {
            return state.cartDataErrors;
        },
        getCurrentDeliveryDateAndTime(state) {
            return state.currentDeliveryDateAndTime;
        },
        getIsOrderCompleted(state) {
            return state.isOrderCompleted;
        },
        getIsAddressCreated(state) {
            return state.isAddressCreated;
        },
        getShippingAddressId(state) {
            return state.shippingAddressId;
        },
        getDeliveryAddressErrors(state) {
            return state.deliveryAddressErrors;
        },
        getIsInvalidPromoCode(state) {
            return state.isInvalidPromoCode;
        },
        getIsSendCartProcessing(state) {
            return state.isSendCartProcessing;
        },
        getIsOrderError(state) {
            return state.isOrderError;
        },
        getIsFetchingProduct(state) {
            return state.isFetchingProduct;
        },
        getIsNotAuthAfterFetching(state) {
            return state.isNotAuthAfterFetching;
        },
        getErrorWithCode(state) {
            return state.errorWithCode;
        },
    },
    mutations: {
        SET_CART_DATA(state, payload) {
            if (payload.length === 0) {
                state.cartData = {
                    total_count: 0,
                    total_discount: 0,
                    total_price: 0,
                    loyalty_give: 0,
                    products: [],
                };
                return;
            }
            const result = { ...payload };
            result.products = result.products.map(product => ({ ...product, prescription_checked: false }));

            state.cartData = result;
        },
        SET_PRESCRIPTION(state, { id, prescription }) {
            let newProduct = state.cartData.products.find(e => e.id === id);
            newProduct.prescription_checked = prescription;
        },
        SET_IS_FETCHING_ALL_CART_PRODUCTS(state, payload) {
            state.isFetchingAllCartProducts = payload;
        },
        SET_IS_FETCHING_PRODUCT(state, payload) {
            state.isFetchingProduct = payload;
        },
        SET_IS_VALID_CART_DATA(state, payload) {
            state.isValidCartData = payload;
        },
        SET_BROWSER_ID(state, payload) {
            state.browserId = payload;
        },
        SET_CITIES(state, payload) {
            state.cities = payload;
        },
        SET_IS_SHOW_ADDED_TO_CART_NOTIFICATION(state, payload) {
            state.isShowAddedToCartNotification = payload;
        },
        SET_ADDRESSES(state, payload) {
            state.addresses = payload;
        },
        SET_DELIVERY_ADDRESS(state, payload) {
            state.deliveryAddress = payload;
        },
        SET_CAN_PROCEED_WITH_ORDER(state, payload) {
            state.canProceedWithOrder = payload;
        },
        SET_PROCEED_WITH_ORDER_ERRORS(state, payload) {
            state.proceedWithOrderErrors = payload;
        },
        SET_STORE_ID(state, value) {
            state.storeId = value;
        },
        SET_DELIVERY_DATE_AND_TIME(state, payload) {
            state.deliveryDateAndTime = payload;
        },
        SET_PROMO_CODE(state, value) {
            state.promoCode = value;
        },
        SET_CART_DATA_ERRORS(state, payload) {
            state.cartDataErrors = payload;
        },
        SET_CURRENT_DELIVERY_DATE_AND_TIME(state, payload) {
            state.currentDeliveryDateAndTime = payload;
        },
        SET_IS_ORDER_COMPLETED(state, payload) {
            state.isOrderCompleted = payload;
        },
        SET_IS_ADDRESS_CREATED(state, payload) {
            state.isAddressCreated = payload;
        },
        SET_SHIPPING_ADDRESS_ID(state, payload) {
            state.shippingAddressId = payload;
        },
        SET_DELIVERY_ADDRESS_ERRORS(state, payload) {
            state.deliveryAddressErrors = payload;
        },
        SET_IS_INVALID_PROMO_CODE(state, payload) {
            state.isInvalidPromoCode = payload;
        },
        SET_NEW_PRICE_AFTER_PROMO_CODE(state, value) {
            if (value.status !== 'success') {
                return;
            }

            const applyDiscount = (product, discountAmount, discountPercentage) => {
                let result = { ...product };
                result.discountAmount = discountAmount;
                result.discountPercentage = discountPercentage;
                result.priceWithDiscount = (Number(product.price) - discountAmount).toFixed(2);
                return result;
            };

            const calculateDiscount = (originalPrice, discountValue, type) => {
                let discountAmount = 0;
                let discountPercentage = 0;

                if (type === 'percent') {
                    discountPercentage = Number(discountValue);
                    discountAmount = (originalPrice * discountPercentage) / 100;
                }

                return { discountAmount, discountPercentage };
            };

            const updateProducts = (products, value) => {
                return products.map(product => {
                    let result = product;
                    const originalPrice = Number(product.price);

                    const { discountAmount, discountPercentage } = calculateDiscount(
                        originalPrice,
                        value.promo_value,
                        value.promo_value_type
                    );

                    const foundProduct = value.products.find(p => p.id === product.id);

                    if (foundProduct) {
                        result = applyDiscount({ ...foundProduct, ...product }, discountAmount, discountPercentage);
                    }

                    return { ...foundProduct, ...result };
                });
            };

            const isGeneralPromo = value.type === 'general';
            const isProductPromo = value.type === 'product';

            let result = [...state.cartData.products];

            if (
                (isProductPromo && value.promo_value_type === 'value') ||
                (isProductPromo && value.promo_value_type === 'percent')
            ) {
                result = updateProducts(result, value);
            }
            if (isGeneralPromo) {
                const res = result.map(product => {
                    let result = null;
                    const originalPrice = Number(product.price);
                    const { discountAmount, discountPercentage } = calculateDiscount(
                        originalPrice,
                        value.promo_value,
                        value.promo_value_type
                    );

                    result = applyDiscount(product, discountAmount, discountPercentage);

                    return result;
                });
                result = updateProducts(res, value);
            }

            state.cartData.products = result;
        },
        SET_CLEAR_CART(state) {
            state.isFetchingAllCartProducts = false;
            state.isValidCartData = false;
            state.cartDataErrors = null;
            state.browserId = null;
            state.cities = null;
            state.addresses = null;
            state.isShowAddedToCartNotification = false;
            state.deliveryAddress = null;
            state.canProceedWithOrder = false;
            state.proceedWithOrderErrors = null;
            state.storeId = null;
            state.deliveryDateAndTime = null;
            state.promoCode = null;
            state.currentDeliveryDateAndTime = null;
            state.isOrderCompleted = false;
            state.isAddressCreated = false;
            state.shippingAddressId = null;
            state.isInvalidPromoCode = false;
        },
        SET_IS_SEND_CART_PROCESSING(state, payload) {
            state.isSendCartProcessing = payload;
        },
        SET_IS_ORDER_ERROR(state, payload) {
            state.isOrderError = payload;
        },
        SET_IS_NOT_AUTH_AFTER_FETCHING(state, payload) {
            state.isNotAuthAfterFetching = payload;
        },
        SET_ERROR_WITH_CODE(state, payload = null) {
            if (payload && payload?.code) {
                state.errorWithCode = payload?.error;
            } else {
                state.errorWithCode = null;
            }
        },
    },
    actions: {
        async addProductToCart({ state, commit, dispatch, rootGetters }, payload) {
            commit('SET_ERROR_WITH_CODE', null);
            const isAuth = rootGetters['auth/isAuth'];
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_IS_FETCHING_PRODUCT', true);

            const config = {
                product_id: payload.id,
                count: payload.count,
            };

            if (!isAuth) {
                config.no_user_id = state.browserId;
            }

            try {
                const data = await this.$axios.$post('/general/cart/add-item/', config);
                commit('SET_PROMO_CODE', null);

                if (!payload?.isCart) {
                    commit('SET_IS_SHOW_ADDED_TO_CART_NOTIFICATION', true);

                    setTimeout(() => {
                        commit('SET_IS_SHOW_ADDED_TO_CART_NOTIFICATION', false);
                    }, 3000);
                }

                commit('SET_CART_DATA', data);

                if (payload.isCart) {
                    await dispatch('validateCartData');
                }
                // await dispatch('fetchAllCartData');
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
            }

            setTimeout(() => {
                commit('SET_IS_FETCHING_PRODUCT', false);
            }, 700);
        },
        async reduceItemPromCart({ commit, dispatch }, payload) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_ERROR_WITH_CODE', null);
            try {
                const data = await this.$axios.$post('/general/cart/reduce-item/', {
                    product_id: payload.id,
                    count: payload.count,
                });
                commit('SET_PROMO_CODE', null);

                commit('SET_CART_DATA', data);
                await dispatch('validateCartData');
            } catch (e) {
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
                console.error(e);
            }
        },
        async removeProductFromCart({ commit, dispatch, state }, productId) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const data = await this.$axios.$post('/general/cart/remove-item/', {
                    product_ids: [productId],
                });
                commit('SET_PROMO_CODE', null);

                commit('SET_CART_DATA', data);
                if (state.cartData.products.length !== 0) {
                    await dispatch('validateCartData');
                }
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
            } finally {
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            }
        },
        async fetchAllCartData({ commit, rootGetters }) {
            const isAuth = rootGetters['auth/isAuth'];
            let browserId = null;
            commit('SET_ERROR_WITH_CODE', null);

            if (process.client) {
                browserId = localStorage?.getItem('browser_id') || '';
            }
            const authToken = this.$cookies.get('token');

            commit('SET_BROWSER_ID', browserId);

            const config = {
                method: 'get',
                url: '/general/cart/fetch-all/',
            };

            if (authToken) {
                config.headers = {
                    Authorization: `Bearer ${authToken}`,
                };
                commit('SET_IS_NOT_AUTH_AFTER_FETCHING', false);
            } else {
                config.url += `?no_user_id=${browserId}`;
                commit('SET_IS_NOT_AUTH_AFTER_FETCHING', true);
            }

            try {
                const { data } = await this.$axios(config);

                if (data.length === 0) {
                    commit('SET_CART_DATA', data);
                    return;
                }

                commit('SET_CART_DATA', data[0]);
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
            }
        },
        async validateCartData({ commit }) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_CART_DATA_ERRORS', null);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const response = await this.$axios.get('/general/cart/validate-products/');
                commit('SET_IS_VALID_CART_DATA', true);
            } catch (e) {
                if (e?.response?.status === 400) {
                    commit('SET_IS_VALID_CART_DATA', false);
                    commit('SET_CART_DATA_ERRORS', e.response?.data?.errors);
                }
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
                console.error(e);
            } finally {
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            }
        },
        async createReservation({ commit }, promoCode) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const response = await this.$axios.post('/general/cart/create-reservation/', {
                    promo_code: promoCode,
                });

                if (response.status === 204) {
                    commit('SET_CAN_PROCEED_WITH_ORDER', true);
                } else {
                    commit('SET_CAN_PROCEED_WITH_ORDER', false);
                }
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
                commit('SET_PROCEED_WITH_ORDER_ERRORS', e?.response?.data?.errors);
                commit('SET_CAN_PROCEED_WITH_ORDER', false);
            } finally {
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            }
        },
        fetchDeliveryDateAndTime: async function ({ commit }, storeId) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const res = await this.$axios({
                    method: 'get',
                    url: `/general/delivery-time/${storeId}/`,
                });

                commit('SET_STORE_ID', storeId);
                commit('SET_DELIVERY_DATE_AND_TIME', res.data);

                return res.data;
            } catch (e) {
                console.error(e);

                commit('SET_ERROR_WITH_CODE', e?.response?.data);

                commit('SET_STORE_ID', null);
            } finally {
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            }
        },
        async fetchPromoCode({ state, commit, dispatch, getters }, promoCode) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_IS_INVALID_PROMO_CODE', false);
            commit('SET_ERROR_WITH_CODE', null);

            const allProductIds = getters.getAllLoyaltyProductIds;

            try {
                await dispatch('fetchAllCartData');

                const res = await this.$axios({
                    method: 'POST',
                    url: `/general/promo-code/update_prices/`,
                    data: {
                        code: promoCode,
                        loyalty_products: allProductIds,
                    },
                });

                commit('SET_NEW_PRICE_AFTER_PROMO_CODE', res.data);
                commit('SET_PROMO_CODE', res.data);
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
                commit('SET_IS_INVALID_PROMO_CODE', true);
            } finally {
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            }
        },
        async fetchCitiesList({ state, commit }) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const response = await this.$axios.get('/base/region/');
                commit('SET_CITIES', response.data);
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
            } finally {
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            }
        },
        async fetchAddressesList({ commit }, cityId) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const response = await this.$axios.get(`/base/locality/?region_id=${cityId}`);
                commit('SET_ADDRESSES', response.data);
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
            } finally {
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            }
        },
        async getDeliveryAddress({ commit }) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const response = await this.$axios.get('/general/cart/get-delivery-addresses/');
                commit('SET_DELIVERY_ADDRESS', response.data);
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
            } finally {
            }
        },
        async createDeliveryAddress({ commit }, payload) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_IS_ADDRESS_CREATED', false);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const { data } = await this.$axios.post('/general/cart/create-delivery-address/', {
                    shipping_city_id: payload.city.id,
                    shipping_street_id: payload.street.id,
                    shipping_building: payload.building,
                    shipping_apartment: payload.apartment,
                    shipping_address_note: payload.comment,
                });
                commit('SET_SHIPPING_ADDRESS_ID', data[0].shipping_address_id);
                commit('SET_CURRENT_DELIVERY_DATE_AND_TIME', data[0]);
                commit('SET_IS_ADDRESS_CREATED', true);
                commit('SET_DELIVERY_ADDRESS_ERRORS', null);
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
                commit('SET_IS_ADDRESS_CREATED', false);

                commit('SET_DELIVERY_ADDRESS_ERRORS', e.response.data);
            } finally {
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            }
        },
        async deleteDeliveryAddress({ commit, dispatch }, addressId) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const response = await this.$axios.post(`/general/cart/delete-delivery-address/`, {
                    shipping_address_id: addressId,
                });
                dispatch('getDeliveryAddress');
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            }
        },
        async mergeCart({ state, commit, dispatch, getters }) {
            try {
                const response = await this.$axios.post('/general/cart/merge-cart/', {
                    no_user_id: state.browserId,
                });

                dispatch('fetchAllCartData');
            } catch (e) {
                console.error(e);
            }
        },
        async sendLoyaltyProducts({ state, commit, dispatch }, { id, active }) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_PROMO_CODE', null);
            commit('SET_IS_INVALID_PROMO_CODE', false);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const { data } = await this.$axios.post('/general/cart/set-loyalty-item/', {
                    product_id: id,
                    active,
                });
                commit('SET_CART_DATA', data);
                await dispatch('validateCartData');
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
            } finally {
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
            }
        },
        async sendOrder({ commit, dispatch }, payload) {
            commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', true);
            commit('SET_IS_SEND_CART_PROCESSING', true);
            commit('SET_ERROR_WITH_CODE', null);

            try {
                const { status, data } = await this.$axios.post('/general/v2/order/create-order/', payload);

                if (status !== 201) {
                    return;
                }

                const payUrl = data?.result?.payUrl;
                if (payUrl) {
                    window.location.href = payUrl;
                } else {
                    await this.$router.push(this.localePath('/cart/success'));
                }

                // if (response.status === 201 && response?.data?.result?.payUrl) {
                //     window.location = response?.data?.result?.payUrl;
                //     return;
                // }
                //
                // if(response.status === 201) {
                //     await this.$router.push({ path: this.localePath('/cart/success') });
                //
                // }
            } catch (e) {
                console.error(e);
                commit('SET_ERROR_WITH_CODE', e?.response?.data);
                commit('SET_IS_ORDER_ERROR', e.response.data || true);
            } finally {
                commit('SET_IS_FETCHING_ALL_CART_PRODUCTS', false);
                commit('SET_IS_SEND_CART_PROCESSING', false);
            }
        },
    },
};

export default store;
